import { Global } from '@emotion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TempoLogo from '@/assets/tempo-logo.svg';
import { LegendFooter } from '../published/LegendFooter';
import { PublishedSwimlane } from '../published/PublishedSwimlane';
import type { PublishedMetadata, PublishingPreferences } from '../published/types';
import { exportTransparentGlobalStyles } from './ExportSwimlanePage.styles';
import { useExportSwimlane } from './useExportSwimlane';

/**
 * Search parameter schema for token-authenticated swimlane export routes.
 *
 * This is an internal route called exclusively by the backend export worker with a JWT token.
 * We use z.coerce.string() to handle query parameters that may be parsed as numbers or booleans
 * by TanStack Router, then rely on component logic to interpret the string values.
 *
 * Required query parameters:
 * - token: JWT export token for authentication
 *
 * Optional query parameters for export customization:
 * - title: '0' to hide title, '1' to show (default: show)
 * - branding: '0' to hide branding/logo, '1' to show (default: show)
 * - date: '0' to hide date, '1' to show (default: show)
 * - legend: '0' to hide legend, '1' to show (default: show)
 * - vision: Custom vision statement text
 * - transparent: '1' to enable transparent background, '0' for opaque (default: opaque)
 */
export const internalSwimlaneExportSearchSchema = z.object({
  token: z.string().min(1, 'Token cannot be empty'),
  title: z.coerce.string().optional(),
  branding: z.coerce.string().optional(),
  date: z.coerce.string().optional(),
  legend: z.coerce.string().optional(),
  vision: z.string().optional(),
  transparent: z.coerce.string().optional(),
});

export type InternalSwimlaneExportSearchParams = z.infer<typeof internalSwimlaneExportSearchSchema>;

export interface ExportSwimlanePageProps {
  /**
   * The viewId from the route parameter
   */
  viewId: number;
  /**
   * The visualizationId from the route parameter
   */
  visualizationId: number;
  /**
   * Search parameters from the URL query string
   */
  searchParams: InternalSwimlaneExportSearchParams;
  /**
   * Whether this is being rendered as a PNG publication template.
   * For export route, this is always true.
   */
  isPngPublicationTemplate?: boolean;
}

/**
 * Helper function to parse boolean query parameters.
 * Converts string '0' to false, '1' to true, undefined to the default value.
 * Any other value is treated as false.
 */
function getBooleanQueryParam(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined) return defaultValue;
  return value === '1';
}

/**
 * Computes the effective preferences for the internal export.
 * Query parameters override the default preferences.
 */
function getEffectivePreferences(
  metadata: PublishedMetadata,
  searchParams: InternalSwimlaneExportSearchParams,
): PublishingPreferences {
  return {
    ...metadata.preferences,
    showTitle: getBooleanQueryParam(searchParams.title, true),
    showTempoBranding: getBooleanQueryParam(searchParams.branding, true),
    showPublishDate: getBooleanQueryParam(searchParams.date, true),
    showLegend: getBooleanQueryParam(searchParams.legend, true),
    showVisionStatement:
      searchParams.vision !== undefined ? true : metadata.preferences.showVisionStatement,
  };
}

/**
 * Export page component for token-authenticated visualizations
 * This component is used for generating exports with JWT token authentication.
 *
 * Unlike PublishedSwimlanePage which loads data from public S3 artifacts,
 * this component uses useExportSwimlane hook which makes an authenticated
 * API call to fetch live data for export rendering.
 *
 * Always in export mode - query parameters control visibility of header/footer elements.
 */
export function ExportSwimlanePage({
  viewId,
  visualizationId,
  searchParams,
  isPngPublicationTemplate = true,
}: ExportSwimlanePageProps) {
  const { t } = useTranslation();
  const result = useExportSwimlane({
    viewId,
    visualizationId,
    token: searchParams.token,
  });

  // Parse transparency preference
  const transparentBackground = getBooleanQueryParam(searchParams.transparent, false);

  // Set export-transparent class on document root for transparent background styling
  useEffect(() => {
    if (transparentBackground) {
      document.documentElement.classList.add('export-transparent');
    }
    // Cleanup when component unmounts or transparentBackground changes to false
    return () => {
      document.documentElement.classList.remove('export-transparent');
    };
  }, [transparentBackground]);

  // Handle loading and error states
  switch (result.status) {
    case 'loading':
      return (
        <output className="flex items-center justify-center h-screen text-xl">
          {t('views.published.loading')}
        </output>
      );

    case 'error':
      return (
        <div
          className="flex items-center justify-center h-screen text-xl text-text-error"
          role="alert"
        >
          {t('views.published.errorLoading', { message: result.message })}
        </div>
      );

    case 'success':
      break; // Fall through to render content
  }

  const { metadata, legendItems } = result.data;

  // Always in export mode for internal route
  const preferences = getEffectivePreferences(metadata, searchParams);

  const visionStatement =
    searchParams.vision !== undefined ? searchParams.vision : metadata.visionStatement;

  // Determine if header section should be shown
  const showHeader = preferences.showTitle || preferences.showVisionStatement;

  // Export mode styles - allow scrolling for screenshot capture
  const rootContainerClass = 'h-screen w-screen flex flex-col';
  const swimlaneContainerClass = 'flex-1';

  // Format publish date with fallback to createdAt
  const publishDateValue = metadata.publishedAt || metadata.createdAt;
  const publishDate = publishDateValue
    ? new Date(publishDateValue).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <>
      {transparentBackground && <Global styles={exportTransparentGlobalStyles} />}
      <div
        className={rootContainerClass}
        style={{ backgroundColor: 'var(--export-bg-color, white)' }}
      >
        {/* Header Section - Title and Vision Statement */}
        {showHeader && (
          <header
            className="flex-none px-8 pt-6 pb-4"
            style={{
              backgroundColor: 'var(--export-bg-color, white)',
            }}
            data-export-target="header"
          >
            {preferences.showTitle && (
              <h1 className="text-2xl font-bold text-text-neutral-primary mb-2">
                {metadata.title}
              </h1>
            )}
            {preferences.showVisionStatement && visionStatement && (
              <div className="max-h-32 overflow-y-auto">
                <p className="text-sm text-text-neutral-secondary whitespace-pre-wrap">
                  {visionStatement}
                </p>
              </div>
            )}
          </header>
        )}

        {/* Swimlane Content */}
        <div className={swimlaneContainerClass}>
          <PublishedSwimlane
            data={result.data}
            isPngPublicationTemplate={isPngPublicationTemplate}
            transparentBackground={transparentBackground}
          />
        </div>

        {/* Footer Section - Legend (centered in export), Publish Date, and Branding */}
        {(preferences.showPublishDate ||
          preferences.showTempoBranding ||
          preferences.showLegend) && (
          <footer
            className="flex-none py-4 w-full"
            style={{
              backgroundColor: 'var(--export-bg-color, white)',
            }}
            data-export-target="footer"
          >
            {/* Legend at top of footer (centered) - always shown if enabled */}
            {preferences.showLegend && legendItems.length > 0 && (
              <nav
                aria-label="Roadmap legend"
                className="w-full mb-3"
                data-component="legend-wrapper"
                data-export-target="footer-legend"
              >
                <LegendFooter items={legendItems} />
              </nav>
            )}

            {/* Bottom row: Publish date (left) and Branding (right) */}
            <div className="flex items-center justify-between px-8">
              <div className="text-sm text-text-neutral-secondary">
                {preferences.showPublishDate && publishDate && (
                  <time dateTime={publishDateValue} data-export-target="footer-date">
                    Published on {publishDate}
                  </time>
                )}
              </div>
              <div className="flex items-center gap-3">
                {preferences.showTempoBranding && (
                  <div className="flex items-center gap-2" data-export-target="footer-branding">
                    <img src={TempoLogo} alt="Tempo" className="h-6 w-6" />
                    <span className="text-sm font-semibold text-text-neutral-primary">
                      Tempo SPM
                    </span>
                  </div>
                )}
              </div>
            </div>
          </footer>
        )}
      </div>
    </>
  );
}
